import { z } from "zod";
import { EntityType } from "../entityType.js";
import { zodQueryStringArray } from "../utils/schema.js";
import {
  PaginationMetaResponseSchema,
  PaginationQuerySchema,
} from "./Pagination.js";

export const tagNameMaxLength = 64;
export const tagDescriptionMaxLength = 500;

export const TagMetadataSchema = z.object({
  color: z.string().optional(),
});

export const TagSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  usageInstructions: z.string(),
  category: z.string(),
  entityTypes: z.array(z.string()),
  metadata: TagMetadataSchema,
  createdAt: z.date(),
  updatedAt: z.date().nullable(),
  removedAt: z.date().nullable(),
  creatorId: z.uuid(),
  organizationId: z.uuid(),
});

export const TagMinimalSchema = z.object({
  id: TagSchema.shape.id,
  name: TagSchema.shape.name,
  color: z.string().optional(),
});

export const FilterTagParamsSchema = z.object({
  q: z.string().optional(),
  ids: z.array(z.string()).optional(),
  categories: z.array(z.string()).optional(),
  skip: z.int().optional(),
  take: z.int().max(100).optional(),
});

export const CreateTagInputSchema = z.object({
  name: z.string().min(1).max(tagNameMaxLength),
  usageInstructions: z.string().min(1).max(tagDescriptionMaxLength),
  category: z.string().max(tagNameMaxLength).optional(),
  metadata: TagMetadataSchema.optional(),
  createdByAssistant: z.boolean().optional(),
});

export const UpdateTagInputSchema = z.object({
  name: z.string().min(1).max(tagNameMaxLength).optional(),
  usageInstructions: z.string().min(1).max(tagDescriptionMaxLength).optional(),
  category: z.string().max(tagNameMaxLength).optional(),
  metadata: TagMetadataSchema.optional(),
});

export type Tag = z.infer<typeof TagSchema>;
export type TagMetadata = z.infer<typeof TagMetadataSchema>;
export type TagMinimal = z.infer<typeof TagMinimalSchema>;
export type FilterTagParams = z.infer<typeof FilterTagParamsSchema>;
export type CreateTagInput = z.infer<typeof CreateTagInputSchema>;
export type UpdateTagInput = z.infer<typeof UpdateTagInputSchema>;

export const CreateTagToolInputSchema = z.object({
  name: CreateTagInputSchema.shape.name.describe(
    "The name of the tag (e.g., 'Important', 'Follow-up')",
  ),
  usageInstructions: CreateTagInputSchema.shape.usageInstructions.describe(
    "When and why to apply this tag.",
  ),
  category: CreateTagInputSchema.shape.category.describe(
    "Optional category to group related tags together.",
  ),
  color: z
    .string()
    .optional()
    .describe(
      "Optional hex color code for the tag (e.g. #228BE6). Picks a random color if omitted.",
    ),
});
export const UpdateTagToolInputSchema = z.object({
  tag: z.string().min(1).describe("The tag's id or current name."),
  name: UpdateTagInputSchema.shape.name.describe("New name for the tag."),
  usageInstructions: UpdateTagInputSchema.shape.usageInstructions.describe(
    "New instructions for when and why to apply this tag.",
  ),
  category: UpdateTagInputSchema.shape.category.describe(
    "New category for the tag.",
  ),
  color: z
    .string()
    .optional()
    .describe("New hex color code for the tag (e.g. #228BE6)."),
});
export type CreateTagToolInput = z.infer<typeof CreateTagToolInputSchema>;
export type UpdateTagToolInput = z.infer<typeof UpdateTagToolInputSchema>;

/**
 * ========================================
 * PUBLIC API SCHEMAS
 * ========================================
 */
export const GetTagsQuerySchema = z.object({
  entity_type: zodQueryStringArray(z.enum(EntityType)),
  ...PaginationQuerySchema.shape,
});

export const TagResponseSchema = z.object({
  ...TagSchema.pick({
    id: true,
    name: true,
    category: true,
    usageInstructions: true,
    entityTypes: true,
  }).shape,
  color: TagMetadataSchema.shape.color,
});

export const GetTagsResponseSchema = z.object({
  ...PaginationMetaResponseSchema.shape,
  items: z.array(TagResponseSchema),
});

export const PostTagRequestSchema = z.object({
  name: CreateTagInputSchema.shape.name,
  usageInstructions: CreateTagInputSchema.shape.usageInstructions,
  category: CreateTagInputSchema.shape.category,
  color: TagMetadataSchema.shape.color,
});
export const PostTagResponseSchema = z.object({
  tag: TagResponseSchema,
});

export const PatchTagRequestSchema = z.object({
  name: UpdateTagInputSchema.shape.name,
  usageInstructions: UpdateTagInputSchema.shape.usageInstructions,
  category: UpdateTagInputSchema.shape.category,
  color: TagMetadataSchema.shape.color,
});
export const PatchTagResponseSchema = z.object({
  tag: TagResponseSchema,
});

export type GetTagsQuery = z.infer<typeof GetTagsQuerySchema>;
export type TagResponse = z.infer<typeof TagResponseSchema>;
export type GetTagsResponse = z.infer<typeof GetTagsResponseSchema>;
export type PostTagRequest = z.infer<typeof PostTagRequestSchema>;
export type PostTagResponse = z.infer<typeof PostTagResponseSchema>;
export type PatchTagRequest = z.infer<typeof PatchTagRequestSchema>;
export type PatchTagResponse = z.infer<typeof PatchTagResponseSchema>;
/**
 * ========================================
 * END PUBLIC API SCHEMAS
 * ========================================
 */
